import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import type { DivisionAnalysis } from "@/lib/db/division-analysis";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitMap } from "@/lib/types/deck-builder";
import { getDivisionImagePath, getCoalitionFlagPath, getCountryFlagPath } from "@/lib/utils/division-images";
import { getUnitImagePath } from "@/lib/utils/unit-images";

interface DivisionAnalysisDisplayProps {
  analysis: DivisionAnalysis | null;
  division?: Division;
  unitMap?: UnitMap;
}

export function DivisionAnalysisDisplay({
  analysis,
  division,
  unitMap = {},
}: DivisionAnalysisDisplayProps) {
  const navigate = useNavigate();

  if (!analysis) {
    if (division) {
      return (
        <Card>
          <CardContent className="space-y-2 p-6">
            <h3 className="text-lg font-semibold">No community analysis yet</h3>
            <p className="text-sm text-muted-foreground">
              There is no community write-up or map entry for {division.name}. Open the{" "}
              <span className="font-medium text-foreground">Briefing</span> tab for automated division metrics, or
              choose another division.
            </p>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold">Please select a division</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Use the division picker above or the map (on larger screens).
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full overflow-auto">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {division && (
                <>
                  <img
                    src={getDivisionImagePath(division.descriptor)}
                    alt={division.name}
                    className="h-8 w-8 object-contain flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <img
                    src={getCountryFlagPath(division.country)}
                    alt={division.country}
                    className="h-6 w-6 object-contain flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </>
              )}
              <CardTitle className="text-xl sm:text-2xl truncate">
                {division?.name || analysis.divisionDescriptor}
              </CardTitle>
            </div>
            {division && (
              <Button
                onClick={() => navigate(`/deck-builder/${division.descriptor}`)}
                className="w-full sm:w-auto flex-shrink-0"
              >
                Build this deck
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap">{analysis.description}</p>
        </CardContent>
      </Card>

      {/* History */}
      {analysis.history && (
        <Card>
          <CardHeader>
            <CardTitle>History</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">{analysis.history}</p>
          </CardContent>
        </Card>
      )}

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Pros */}
        <Card className="bg-green-500/10 border-green-500/20">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400">Pros</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-none space-y-1.5">
              {analysis.pros.map((pro) => (
                <li key={pro.id} className="flex gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">+</span>
                  <span>{pro.bulletpoint}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Cons */}
        <Card className="bg-red-500/10 border-red-500/20">
          <CardHeader>
            <CardTitle className="text-red-700 dark:text-red-400">Cons</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-none space-y-1.5">
              {analysis.cons.map((con) => (
                <li key={con.id} className="flex gap-2">
                  <span className="text-red-600 dark:text-red-400 font-bold">−</span>
                  <span>{con.bulletpoint}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Units */}
      {analysis.unitDescriptors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Units</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {analysis.unitDescriptors.map((unitDesc) => {
                const unit = unitMap[unitDesc.descriptorId];
                if (!unit) return null;

                return (
                  <div
                    key={unitDesc.id}
                    className="bg-card rounded-xl flex flex-col items-center cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden"
                    onClick={() => navigate(`/units/${unit.descriptorName}`)}
                  >
                    <div className="relative w-full aspect-[360/184] flex items-center justify-center overflow-hidden bg-muted/20">
                      <img
                        src={getUnitImagePath(unit.descriptorName)}
                        alt={unit.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                    <h5 className="text-xs font-medium text-center p-1.5">{unit.name}</h5>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

